import React, { useEffect, useMemo, useState } from "react";
/* ============================ CONFIG ============================ */
// Usage example:
// <NbaData title="NBA — All Players" dataUrl="/data/nba/classic/latest/data.json" metaUrl="/data/nba/classic/latest/meta.json" />
const SHOW_TEAM_LOGOS = true;
const HIDE_PLAYER_INFO_LABEL = true; // for the band header row
// logos are served from /public/logos/nba/XXX.png -> /logos/nba/XXX.png
const LOGO_BASE = "/logos/nba";
const LOGO_EXT = "png";
// Normalize old/alt abbreviations to your artwork names
const TEAM_ABBR_MAP = {
  NOH: "NOP",
  NOP: "NOP",
  NOR: "NOP",
  CHO: "CHA",
  CHA: "CHA",
  PHO: "PHX",
  PHX: "PHX",
  GSW: "GSW",
  GS: "GSW",
  SAS: "SAS",
  SA: "SAS",
  UTA: "UTA",
  UTH: "UTA",
  NY: "NYK",
  NYK: "NYK",
  NJN: "BKN",
  BRK: "BKN",
  BKN: "BKN",
  WAS: "WAS",
  WSH: "WAS",
  LAC: "LAC",
  LAClippers: "LAC",
  LAL: "LAL",
  NOPelicans: "NOP",
};
// Two-row header (bands + columns). You can tweak labels/order here.
const BANDS = [
  {
    name: "Player Info",
    cols: ["Pos", "Min", "Player", "DK Sal", "FD Sal"],
  },
  {
    name: "Matchup",
    cols: ["Team", "vs", "Opp", "Home", "Time"],
  },
  {
    name: "Vegas",
    cols: ["O/U", "I. Tot", "Line"],
  },
  {
    name: "Pace",
    cols: ["T Pace", "O Pace", "AVG"],
  },
  {
    name: "Player Advanced Stats",
    cols: ["Min PG", "% Min", "DK PPM", "FD PPM", "Usg%", "O Rtg", "D Rtg"],
  },
  {
    name: "Stats Per Game",
    cols: ["PPG", "APG", "RPG", "SPG", "BPG", "3PM", "TOV"],
  },
];
/* ============================ UI THEME (matches NFL pos page) ============================ */
const LIGHT_THEME = {
  radius: "rounded-2xl",
  headerBg: "bg-slate-50",
  bandBg: "bg-slate-100",
  zebraOdd: "bg-white",
  zebraEven: "bg-slate-50",
  border: "border-slate-200",
  bandBorder: "border-slate-300",
  text: "text-slate-900",
  subtext: "text-slate-600",
  card: "border border-slate-200 bg-white shadow-sm",
  page: "min-h-screen bg-slate-100 text-slate-900",
};
const DARK_THEME = {
  radius: "rounded-2xl",
  headerBg: "bg-slate-900",
  bandBg: "bg-slate-900",
  zebraOdd: "bg-slate-950",
  zebraEven: "bg-slate-900",
  border: "border-slate-800/80",
  bandBorder: "border-slate-700/80",
  text: "text-slate-100",
  subtext: "text-slate-400",
  card:
    "border border-slate-800 bg-slate-900/95 shadow-sm",
  page: "min-h-screen bg-slate-950 text-slate-100",
};
const CELL_PAD = "px-0.5 py-0.5";
const HEADER_PAD = "px-2 py-1.5";
const TEXT_SZ = "text-[11.5px]";
/* ============================ HELPERS ============================ */
const norm = (s) => String(s ?? "").trim();
const lower = (s) => norm(s).toLowerCase();
const keynorm = (s) => lower(String(s).replace(/[\s._%/()]/g, ""));
function parseNumericLike(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  if (/%$/.test(s)) {
    const n = Number(s.replace(/%/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}
function fmtTime(s) {
  const raw = norm(s);
  if (!raw) return "";
  if (/\b(am|pm)\b/i.test(raw))
    return raw
      .replace(/\s+/g, "")
      .toUpperCase()
      .replace("AM", " AM")
      .replace("PM", " PM");
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return raw;
  let hh = Number(m[1]);
  const mm = m[2];
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = ((hh + 11) % 12) + 1;
  return `${hh}:${mm} ${ampm}`;
}
function timeToMinutes(s) {
  const raw = norm(s);
  if (!raw) return null;
  const ap = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ap) {
    let h = Number(ap[1]),
      m = Number(ap[2]);
    const pm = /pm/i.test(ap[3]);
    if (h === 12) h = pm ? 12 : 0;
    else if (pm) h += 12;
    return h * 60 + m;
  }
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
function fmtCellValue(col, val) {
  const v = norm(val);
  if (!v) return "";
  // keep explicit percent strings as-is
  if (/%$/.test(v)) return v;
  // ✅ only convert "% Min" to a percentage — not "Min" or "Minutes"
  if (/^%+\s*min$/i.test(String(col)) || /^percent\s*min$/i.test(String(col))) {
    const n = parseNumericLike(v);
    if (n == null) return v;
    const pct = Math.round(n * 100);
    return `${pct}%`;
  }
  if (/^time$/i.test(col)) return fmtTime(v);
  if (/\b(sal|salary)\b/i.test(col)) {
    const n = parseNumericLike(v);
    return n == null ? v : Math.round(n).toLocaleString();
  }
  const n = parseNumericLike(v);
  if (n == null) return v;
  const f = n.toFixed(1);
  return f.endsWith(".0") ? f.slice(0, -2) : f;
}
/* ----- fetchers ----- */
function useJson(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const arr = Array.isArray(j) ? j : j?.rows || j?.data || [];
        if (alive) setData(arr);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, loading, err };
}
function useMeta(url) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    if (!url) return;
    let ok = true;
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error();
        const j = await r.json();
        if (ok) setMeta(j);
      } catch {
        if (ok) setMeta(null);
      }
    })();
    return () => {
      ok = false;
    };
  }, [url]);
  return meta;
}
/* ============================ LOGOS ============================ */
function fixAbbr(code) {
  const c = String(code || "").toUpperCase();
  return TEAM_ABBR_MAP[c] || c;
}
function TeamWithLogo({ code }) {
  const c = fixAbbr(code);
  if (!c) return null;
  if (!SHOW_TEAM_LOGOS) return <span>{c}</span>;
  const src = `${LOGO_BASE}/${c}.${LOGO_EXT}`;
  return (
    <span className="inline-flex items-center gap-1">
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={src}
        className="h-4 w-4 shrink-0"
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
      <span>{c}</span>
    </span>
  );
}
/* ============================ HEATMAP RULES ============================ */
// Never heat these columns at all (no color)
const EXCLUDE_HEAT = [/^line$/i, /^spread$/i];
// Always heat as higher = better
const ALWAYS_HEAT_HIGH = [/^o\/?u$/i, /^i\.?\s*tot(al)?$/i];
// Direction rules for other columns
const DIR_RULES = [
  // higher is better
  { re: /^(ppg|apg|rpg|spg|bpg|3pm)$/i, dir: "higher" },
  { re: /^(dk\s*ppm|fd\s*ppm)$/i, dir: "higher" },
  { re: /^usg%$/i, dir: "higher" },
  { re: /^o\s*rtg$/i, dir: "higher" },
  { re: /^pace$|^t\s*pace$|^o\s*pace$|^avg$/i, dir: "higher" },
  { re: /^vegas$/i, dir: "higher" },
  // NEW: color these as higher = better
  { re: /^min\s*pg$/i, dir: "higher" },
  { re: /^%?\s*min$/i, dir: "higher" }, // "% Min" or "Min" with percent
  // lower is better
  { re: /^d\s*rtg$/i, dir: "lower" },
  { re: /^tov$/i, dir: "lower" },
  { re: /^dk\s*sal(ary)?$/i, dir: "lower" },
  { re: /^fd\s*sal(ary)?$/i, dir: "lower" },
];
function directionForColumn(colName) {
  const k = String(colName).trim();
  if (EXCLUDE_HEAT.some((rx) => rx.test(k))) return null; // ← no heat for Line/Spread
  if (ALWAYS_HEAT_HIGH.some((rx) => rx.test(k))) return "higher";
  for (const { re, dir } of DIR_RULES) if (re.test(k)) return dir;
  return null;
}
function heatTextColor(min, max, v, dir, theme = "dark") {
  if (v == null || min == null || max == null || min === max || !dir) {
    return null;
  }
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  const isDark = theme === "dark";
  // orange/red -> yellow -> green
  if (t < 0.5) {
    const u = t / 0.5;
    const h = 8 + u * 42;
    const s = isDark ? 90 : 85;
    const l = isDark ? 70 - u * 8 : 48 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  } else {
    const u = (t - 0.5) / 0.5;
    const h = 50 + u * 70;
    const s = isDark ? 88 : 80;
    const l = isDark ? 62 + u * 8 : 44 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
}
/* ============================ CSV EXPORT ============================ */
function exportToCsv(filename, rows, columns) {
  const header = columns.join(",");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const raw = row[col];
          const cell = raw === undefined || raw === null ? "" : String(raw);
          const escaped = '"' + cell.replaceAll('"', '""') + '"';
          return escaped;
        })
        .join(",")
    )
    .join("\n");
  const csv = header + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
/* ============================ MAIN ============================ */
export default function NbaData({
  title = "NBA — All Players",
  dataUrl,
  metaUrl,
}) {
  const { data, loading, err } = useJson(dataUrl);
  const meta = useMeta(metaUrl);
  // theme toggle (same as NFL pos page)
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  const T = isDark ? DARK_THEME : LIGHT_THEME;
  // Updated badge from meta.json
  const updatedStamp = useMemo(() => {
    const art = meta?.artifacts?.find?.((x) =>
      String(x.path || "").endsWith(dataUrl)
    );
    const raw =
      art?.last_updated || art?.tags?.last_updated || meta?.last_updated;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [meta, dataUrl]);
  // visible columns are the BANDS order, but only those that actually exist in the data
  const allColumns = useMemo(() => {
    const available = data.length ? Object.keys(data[0]).filter((c) => !["pace", "vegas"].includes(c.trim().toLowerCase())) : [];
    const cols = BANDS.flatMap((b) => b.cols.filter((c) => available.includes(c)));
    // fallback if dataset has extra columns (append them at the end)
    const extras = available.filter((c) => !cols.includes(c));
    return [...cols, ...extras];
  }, [data]);
  const bandMeta = useMemo(() => {
    const spans = [];
    let start = 0;
    for (const band of BANDS) {
      const cols = band.cols.filter((c) => allColumns.includes(c));
      if (cols.length) {
        spans.push({ name: band.name, start, span: cols.length });
        start += cols.length;
      }
    }
    // extras band
    const extraCount = allColumns.length - start;
    if (extraCount > 0) spans.push({ name: "Misc", start, span: extraCount });
    return spans;
  }, [allColumns]);
  // search
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = lower(q);
    if (!s) return data;
    return data.filter((r) => {
      const name = String(r.Player || "");
      const team = String(r.Team || "");
      const opp = String(r.Opp || r.OPP || "");
      return (
        lower(name).includes(s) ||
        lower(team).includes(s) ||
        lower(opp).includes(s)
      );
    });
  }, [data, q]);
  // heat stats
  const heatStats = useMemo(() => {
    const stats = {};
    if (!filtered.length) return stats;
    const cols = allColumns;
    for (const col of cols) {
      const dir = directionForColumn(col);
      if (!dir) continue;
      let min = Infinity,
        max = -Infinity;
      for (const row of filtered) {
        const n = parseNumericLike(row[col]);
        if (n == null) continue;
        if (n < min) min = n;
        if (n > max) max = n;
      }
      if (min !== Infinity && max !== -Infinity) stats[col] = { min, max, dir };
    }
    return stats;
  }, [filtered, allColumns]);
  // sorting
  const [sort, setSort] = useState({ key: "Player", dir: "asc" });
  useEffect(() => {
    const dk = allColumns.find((c) => /\bdk\s*sal(ary)?\b/i.test(c));
    if (dk) setSort({ key: dk, dir: "desc" });
    else setSort({ key: allColumns[0] || "Player", dir: "asc" });
  }, [allColumns]);
  const onSort = (keyName) => {
    setSort((prev) =>
      prev.key === keyName
        ? { key: keyName, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: keyName, dir: "desc" }
    );
  };
  function compareCells(a, b, keyName) {
    if (/^time$/i.test(keyName)) {
      const av = timeToMinutes(a[keyName]);
      const bv = timeToMinutes(b[keyName]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    }
    if (/(^|\b)(sal|salary)(\b|$)/i.test(keyName)) {
      const av = parseNumericLike(a[keyName]);
      const bv = parseNumericLike(b[keyName]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    }
    const av = parseNumericLike(a[keyName]);
    const bv = parseNumericLike(b[keyName]);
    if (av != null && bv != null) return av - bv;
    const sa = String(a[keyName] ?? "");
    const sb = String(b[keyName] ?? "");
    return sa.localeCompare(sb, undefined, { sensitivity: "base" });
  }
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key: k, dir } = sort;
    const sgn = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => sgn * compareCells(a, b, k));
    return arr;
  }, [filtered, sort]);
  // player column index for sticky effect (3rd col in Player Info band)
  const playerColIndex = useMemo(
    () => allColumns.indexOf("Player"),
    [allColumns]
  );
  const cellCls = `${CELL_PAD} text-center whitespace-nowrap overflow-hidden text-ellipsis ${T.text}`;
  const headerCls = `${HEADER_PAD} font-semibold text-center whitespace-nowrap cursor-pointer select-none ${T.text}`;
  return (
    <div className={T.page}>
      <div className="w-full px-4 md:px-6 py-5">
        {/* Header row: title + small updated + theme toggle (matches NFL pos layout) */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">{title}</h1>
            <span className={`text-xs italic ${T.subtext}`}>Updated: {updatedStamp ?? "—"}</span>
          </div>
          {/* theme toggle pill */}
          <div className="flex items-center gap-3">
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark
                  ? "bg-slate-900/90 ring-slate-700/80"
                  : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </div>
        <section aria-label="Player data" className={`${T.radius} ${T.card}`}>
          <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 ${T.border}`}>
            <h2 className="text-sm font-semibold">Player Data <span className={`ml-2 text-xs font-normal ${T.subtext}`}>{!loading && !err ? `${sorted.length.toLocaleString()} players` : ""}</span></h2>
          <button
            onClick={() =>
              exportToCsv(
                `${title.replaceAll(" ", "_")}.csv`,
                sorted,
                allColumns
              )
            }
            className="h-8 rounded-md bg-sky-500 px-3 text-xs font-semibold text-slate-950 hover:bg-sky-400"
          >
            Export CSV
          </button>
          </div>
          <div className="px-4 py-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search player / team / opp…"
            className={
              "h-8 w-full sm:w-72 rounded-md border px-3 text-xs focus:ring-2 focus:ring-sky-500 focus:outline-none " +
              (isDark
                ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400")
            }
          />
          </div>
        {/* TABLE CARD (same pattern as NFL pos) */}
        <div
          className="overflow-auto max-h-[70vh] rounded-b-2xl overscroll-contain"
        >
          <table
            className={`w-full border-separate ${TEXT_SZ}`}
            style={{ borderSpacing: 0 }}
          >
            {allColumns.length > 0 && (
              <thead className="sticky top-0 z-40">
                {/* band row */}
                <tr
                  className={`${T.bandBg} text-[11px] font-bold uppercase ${T.text}`}
                >
                  {bandMeta.map((g, idx) => (
                    <th
                      key={`${g.name}-${g.start}`}
                      colSpan={g.span}
                      className={`px-2 py-1 text-center border-b ${T.bandBorder} ${
                        idx === 0 ? "first:rounded-tl-2xl" : ""
                      } ${
                        idx === bandMeta.length - 1
                          ? "last:rounded-tr-2xl"
                          : ""
                      }`}
                    >
                      {HIDE_PLAYER_INFO_LABEL && g.name === "Player Info"
                        ? ""
                        : g.name}
                    </th>
                  ))}
                </tr>
                {/* column headers */}
                <tr className={`${T.headerBg}`}>
                  {allColumns.map((c, i) => {
                    const isBandEnd = bandMeta.some(
                      (b) => b.start + b.span - 1 === i
                    );
                    const isPlayer = i === playerColIndex;
                    return (
                      <th
                        key={c}
                        className={[
                          headerCls,
                          isBandEnd
                            ? `border-r-2 ${T.bandBorder}`
                            : `border-r ${T.border}`,
                          isPlayer
                            ? `text-left sticky left-0 z-50 ${T.headerBg} shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)]`
                            : "",
                        ].join(" ")}
                        onClick={() => onSort(c)}
                        title="Click to sort"
                      >
                        <span className="inline-flex items-center gap-1">
                          <span>{c}</span>
                          {sort.key === c ? (
                            <span className="text-slate-400">
                              {sort.dir === "desc" ? "▼" : "▲"}
                            </span>
                          ) : (
                            <span className="text-slate-500/30">▲</span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody>
              {loading && (
                <tr>
                  <td
                    className={`${cellCls} ${T.subtext}`}
                    colSpan={allColumns.length}
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {err && (
                <tr>
                  <td
                    className={`${cellCls} text-red-500`}
                    colSpan={allColumns.length}
                  >
                    Failed to load: {err}
                  </td>
                </tr>
              )}
              {!loading &&
                !err &&
                sorted.map((row, rIdx) => {
                  const rowBg = rIdx % 2 ? T.zebraEven : T.zebraOdd;
                  return (
                    <tr
                    key={rIdx}
                    className={`${rowBg} hover:bg-sky-500/10 transition-colors`}
                  >
                    {allColumns.map((c, i) => {
                      const raw = row[c];
                      const isTeam = keynorm(c) === keynorm("Team");
                      const isOpp = keynorm(c) === keynorm("Opp");
                      const isPlayer = i === playerColIndex;
                      const content =
                        isTeam || isOpp ? (
                          <TeamWithLogo code={raw} />
                        ) : (
                          fmtCellValue(c, raw)
                        );
                      const borders = bandMeta.some(
                        (b) => b.start + b.span - 1 === i
                      )
                        ? `border-r-2 ${T.bandBorder}`
                        : `border-r ${T.border}`;
                    const stat = heatStats[c];
                    const numVal = parseNumericLike(raw);
                    const textColor = stat
                      ? heatTextColor(
                          stat.min,
                          stat.max,
                          numVal,
                          stat.dir,
                          theme
                        )
                      : null;
                      // Sticky Player column - BODY CELLS
                      if (isPlayer) {
                        return (
                          <td
                            key={`${c}-${rIdx}`}
                            className={[
                              cellCls,
                              "text-left font-medium whitespace-nowrap overflow-hidden text-ellipsis",
                              "sticky left-0 z-30",
                              rowBg,
                              "min-w-[16ch] md:min-w-[18ch]",
                              "shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)]",
                              borders,
                            ].join(" ")}
                            title={String(raw ?? "")}                          >
                            {content}
                          </td>
                        );
                      }
                      // All other cells
                  return (
                    <td
                      key={`${c}-${rIdx}`}
                      className={[
                        cellCls,
                        isPlayer ? "text-left font-medium" : "text-center",
                        borders,
                      ].join(" ")}
                    >
                      {isTeam || isOpp ? (
                        content
                      ) : (
                        <span
                          style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                        >
                          {content}
                        </span>
                      )}
                    </td>
                  );
                    })}
                  </tr>
                   );
                 })}
              {!loading && !err && sorted.length === 0 && (
                <tr>
                  <td
                    className={`${cellCls} ${T.subtext}`}
                    colSpan={allColumns.length}
                  >
                    No rows match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </section>
      </div>
    </div>
  );
}
